import React, { useEffect, useState } from 'react';
import {
  updateDoc,
  setDoc,
  doc,
} from 'firebase/firestore';

import {
  firebaseConfig,
  app,
  auth,
  db,
  functions,
  storage,
  storageRef,
  FirebaseFieldValue,
  FirebaseTimestamp,
  firebaseAuth,
  firebaseImport,
} from '@/js/external-js/config/main_config';

import { httpsCallable } from 'firebase/functions';
import { useTenancy, useOrgStructure, useUsers } from '@/contexts/TenancyContext';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

import { Loader2, Pencil, Trash2, UserPlus2, LayoutGrid, Table2 } from 'lucide-react';
import { DataTable } from '@/components/ui/datatable';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Building2, Shield } from 'lucide-react';
import { mspService } from '@/services/mspService';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: Record<string, boolean>;
  // DED-605: Removed team field - Team Settings should not be in User Management
  businessUnit?: string;
  ismsRole?: string;
  ismsCommittee?: string;
  // DED-1250: MSP user flag
  isMspUser?: boolean;
  mspType?: 'techPartner' | 'businessPartner';
}

// DED-1250: MSP details structure from customer document
interface MspPartnerDetails {
  impersonationAllowed: boolean;
  msp: string;
}

interface MspDetails {
  techPartner?: MspPartnerDetails;
  businessPartner?: MspPartnerDetails;
}

// User management functionality is now handled by TenancyContext

export default function Users() {
  const { activeTenancy } = useTenancy();
  const { users, loading } = useUsers();
  const plan = activeTenancy?.plan || 'default';
  const isIsoPlan = plan === 'default' || plan === 'Business Plan' || plan === 'Enterprise Plan';
  // DED-1444: Check if Starter plan to hide ISMS columns
  const isStarterPlan = plan?.toLowerCase().includes('starter');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editDrawer, setEditDrawer] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');

  // DED-1250: MSP management state
  const [mspDetails, setMspDetails] = useState<MspDetails | null>(null);
  const [editMspUser, setEditMspUser] = useState<User | null>(null);
  const [mspName, setMspName] = useState<string>('');
  const [mspLoading, setMspLoading] = useState(false);

  // Track the tenancy ID to reset state when it changes
  const [currentTenancyId, setCurrentTenancyId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('createUser') === 'true') {
      setTimeout(() => setShowAddModal(true), 500);
    }
  }, []);

  // Reset state when tenancy changes
  useEffect(() => {
    if (activeTenancy?.id && activeTenancy.id !== currentTenancyId) {
      console.log('Tenancy changed, resetting user management state');
      // Reset pagination
      setCurrentPage(1);
      // Clear search term
      setSearchTerm('');
      // Close any open modals or drawers
      setEditUser(null);
      setEditDrawer(false);
      setShowAddModal(false);
      setShowAddDrawer(false);
      // Update current tenancy ID
      setCurrentTenancyId(activeTenancy.id);
      // DED-1250: Reset MSP state
      setMspDetails(null);
      setEditMspUser(null);
      setMspName('');
    }
  }, [activeTenancy?.id, currentTenancyId]);

  // DED-1250: Fetch MSP details when tenancy changes
  useEffect(() => {
    const fetchMspDetails = async () => {
      if (!activeTenancy?.id) return;
      
      try {
        // Fetch customer document to get mspDetails
        const { getDoc, doc } = await import('firebase/firestore');
        const customerRef = doc(db, 'customers', activeTenancy.id);
        const customerSnap = await getDoc(customerRef);
        
        if (customerSnap.exists()) {
          const data = customerSnap.data();
          if (data.mspDetails) {
            setMspDetails(data.mspDetails);
          } else {
            setMspDetails(null);
          }
        }
      } catch (error) {
        console.error('Error fetching MSP details:', error);
      }
    };
    
    fetchMspDetails();
  }, [activeTenancy?.id]);

  const [showDeleteUserDialogState, setShowDeleteUserDialogState] = useState<{show: boolean, userID: string | null}>({
    show: false,
    userID: null,
  });
  const deleteUserConfirmed = async (confirmed: boolean): Promise<void> => {
    if (!confirmed) {
      setShowDeleteUserDialogState({show: false, userID: null});
      // console.log('[ASD]', `Delete user cancelled`);
      return;
    }
    // console.log('[ASD]', `Delete user confirmed!`, showDeleteUserDialogState.userID)
    await deleteUser(showDeleteUserDialogState.userID);
    setShowDeleteUserDialogState({show: false, userID: null});
  };

  const deleteUser = async (userId: string) => {
    // if (!confirm('Delete this user?')) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`${import.meta.env.VITE_cloudFunctionURL}/deiterateInternalAPI/users/delete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Company-ID': activeTenancy.id,
        },
        body: JSON.stringify({ userID: userId, companyID: activeTenancy.id }),
      });

      // The users list will be automatically updated via the TenancyContext listener
    } catch (err: any) {
      alert(err.message);
    }
  };

  // Define columns for table view
  // DED-1444: Conditionally include ISMS Role column based on plan
  const columns: ColumnDef<User>[] = React.useMemo(
    () => {
      const baseColumns: ColumnDef<User>[] = [
        {
          accessorKey: 'firstName',
          header: 'First Name',
          cell: ({ row }) => <div className="font-medium">{row.original.firstName}</div>,
        },
        {
          accessorKey: 'lastName',
          header: 'Last Name',
          cell: ({ row }) => <div className="font-medium">{row.original.lastName}</div>,
        },
        {
          accessorKey: 'email',
          header: 'Email',
          cell: ({ row }) => <div className="text-muted-foreground">{row.original.email}</div>,
        },
        {
          accessorKey: 'businessUnit',
          header: 'Business Unit',
          cell: ({ row }) => <div className="text-sm">{row.original.businessUnit || '-'}</div>,
        },
      ];

      // DED-1444: Only add ISMS Role and Committee columns for non-Starter plans
      if (!isStarterPlan) {
        baseColumns.push({
          accessorKey: 'ismsRole',
          header: 'ISMS Role',
          cell: ({ row }) => <div className="text-sm">{row.original.ismsRole || '-'}</div>,
        });
        // DED-1514: Add ISMS Committee column
        baseColumns.push({
          accessorKey: 'ismsCommittee',
          header: 'Committee',
          cell: ({ row }) => <div className="text-sm">{row.original.ismsCommittee || '-'}</div>,
        });
      }

      // Actions column always at the end
      baseColumns.push({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          // DED-1250: Check if this is an MSP user
          const isMspUser = row.original.isMspUser;
          
          if (isMspUser) {
            return (
              <div className="flex gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => setEditMspUser(row.original)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>
            );
          }
          
          return (
            <div className="flex gap-2">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => setEditUser(row.original)}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                size="icon"
                variant="destructive"
                className="h-8 w-8"
                onClick={() => setShowDeleteUserDialogState({show: true, userID: row.original.id})}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          );
        },
      });

      return baseColumns;
    },
    [activeTenancy.id, isStarterPlan]
  );


  // DED-1250: Create MSP user entries from mspDetails
  const mspUsers = React.useMemo((): User[] => {
    const mspUserList: User[] = [];
    
    if (mspDetails?.techPartner?.msp) {
      mspUserList.push({
        id: `msp-tech-${mspDetails.techPartner.msp}`,
        email: '',
        firstName: 'Tech Partner',
        lastName: 'MSP',
        roles: {},
        isMspUser: true,
        mspType: 'techPartner',
      });
    }
    
    if (mspDetails?.businessPartner?.msp) {
      mspUserList.push({
        id: `msp-business-${mspDetails.businessPartner.msp}`,
        email: '',
        firstName: 'Business Partner',
        lastName: 'MSP',
        roles: {},
        isMspUser: true,
        mspType: 'businessPartner',
      });
    }
    
    return mspUserList;
  }, [mspDetails]);

  const filteredUsers = React.useMemo(() => {
    // Filter regular users (exclude any users with "MSP" in their name to avoid duplicates)
    // DED-605: Removed team from search filter - Team Settings should not be in User Management
    const regularUsers = users.filter((u) => {
      const match = `${u.firstName} ${u.lastName} ${u.email} ${u.businessUnit || ''}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      // Exclude any user with "MSP" in firstName or lastName (case-insensitive)
      const hasMspInName = 
        u.firstName?.toLowerCase().includes('msp') || 
        u.lastName?.toLowerCase().includes('msp');
      return match && !hasMspInName;
    });
    
    // DED-1250: Include MSP users if they match the search term
    const filteredMspUsers = mspUsers.filter((u) => {
      if (!searchTerm) return true;
      return `${u.firstName} ${u.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    });
    
    return [...regularUsers, ...filteredMspUsers];
  }, [users, searchTerm, mspUsers]);

  // Calculate pagination values based on filtered results
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const paginatedUsers = React.useMemo(() => {
    return filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [filteredUsers, currentPage, pageSize]);

  // Ensure we never stay on a blank page after filtering/searching
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  return (
    <div className="p-6 glass-card">
      <div className="flex justify-between mb-6 items-center">
        <h1 className="text-2xl font-semibold">Users</h1>
        <div className="hidden sm:block">
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogTrigger asChild>
              <Button><UserPlus2 className="w-4 h-4 mr-2" /> Add User</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px] bg-white dark:bg-secondary">
              <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
              <UserForm
                plan={activeTenancy.plan}
                isDrawer={false}
                companyId={activeTenancy.id}
                onSuccess={() => setShowAddModal(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
        <div className="sm:hidden">
          <Drawer open={showAddDrawer} onOpenChange={setShowAddDrawer}>
            <DrawerTrigger asChild>
              <Button><UserPlus2 className="w-4 h-4 mr-2" /> Add User</Button>
            </DrawerTrigger>
            <DrawerContent className="bg-white dark:bg-secondary px-[12px] py-[12px] mt-[0px]">
              <UserForm
                plan={activeTenancy.plan}
                isDrawer={true}
                companyId={activeTenancy.id}
                onSuccess={() => setShowAddDrawer(false)}
              />
            </DrawerContent>
          </Drawer>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <Input
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-64 hover:bg-transparent focus:bg-transparent"
        />
        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div className="inline-flex items-center border rounded-lg p-1 bg-muted/50">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className="h-8"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="h-8"
            >
              <Table2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex gap-2 text-muted-foreground">
          <Loader2 className="animate-spin h-4 w-4" /> Loading users...
        </div>
      ) : viewMode === 'table' ? (
        <DataTable
          columns={columns}
          data={filteredUsers}
          pageSize={20}
        />
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"> */}
            {paginatedUsers.map((user) => (
              <div key={user.id} className="border rounded-md p-4 flex flex-col justify-between bg-white dark:bg-secondary">
                <div>
                  <div className="font-semibold">{user.firstName} {user.lastName}</div>
                  <div className="text-sm text-muted-foreground truncate">{user.email}</div>
                  {/* DED-605: Removed team display - Team Settings should not be in User Management */}
                  {user.businessUnit && (
                    <div className="text-sm text-muted-foreground">{user.businessUnit}</div>
                  )}
                </div>
                <div className="flex justify-end mt-4 gap-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="delete-user-btn"
                    onClick={() => deleteUser(user.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Button className="sm:hidden" size="icon" onClick={() => setEditDrawer(user)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button className="hidden sm:flex" size="icon" onClick={() => setEditUser(user)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination controls - only show in card view */}
          {viewMode === 'cards' && (
            <div className="mt-4 flex items-center justify-between">
              {/* Spacer for alignment */}
              <div className="w-32" />

              {/* Pagination buttons - centered */}
              <div className="flex gap-2">
                {(() => {
                  const pages: (number | string)[] = [];

                  // Always show first page
                  pages.push(1);

                  if (totalPages <= 7) {
                    // Show all pages if 7 or fewer
                    for (let i = 2; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Smart pagination with ellipsis
                    if (currentPage > 3) {
                      pages.push('ellipsis-start');
                    }

                    // Show pages around current page
                    const start = Math.max(2, currentPage - 1);
                    const end = Math.min(totalPages - 1, currentPage + 1);

                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }

                    if (currentPage < totalPages - 2) {
                      pages.push('ellipsis-end');
                    }

                    // Always show last page
                    pages.push(totalPages);
                  }

                  return pages.map((page) => {
                    if (typeof page === 'string') {
                      return (
                        <span key={page} className="flex items-center px-2 text-muted-foreground">
                          ...
                        </span>
                      );
                    }

                    return (
                      <Button
                        key={page}
                        variant={page === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    );
                  });
                })()}
              </div>

              {/* Per page selector - right aligned */}
              <div className="flex items-center gap-2">
                <Label>Per page</Label>
                <Select value={String(pageSize)} onValueChange={(v) => {
                  setPageSize(Number(v));
                  setCurrentPage(1);
                }}>
                  <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </>
      )}

      <div className="hidden sm:block">
        <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
          <DialogContent className="sm:max-w-[425px] bg-white dark:bg-secondary">
            <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
            {editUser && (
              <UserForm
                isDrawer={false}
                editData={editUser}
                companyId={activeTenancy.id}
                onSuccess={() => setEditUser(null)}
                plan={activeTenancy.plan}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
      <div className="sm:hidden">
        <Drawer open={!!editDrawer} onOpenChange={() => setEditDrawer(false)}>
          <DrawerContent className="bg-white dark:bg-secondary px-[12px] py-[12px] mt-0">
            {editDrawer && (
              <UserForm
                isDrawer={true}
                editData={editDrawer}
                companyId={activeTenancy.id}
                onSuccess={() => setEditDrawer(false)}
                plan={activeTenancy.plan}
              />
            )}
          </DrawerContent>
        </Drawer>
      </div>
      <DeleteUserConfirmationDialog callback={(confirmed) => deleteUserConfirmed(confirmed)} show={showDeleteUserDialogState.show} />
      
      {/* DED-1250: MSP Edit Dialog */}
      <MspEditDialog
        open={!!editMspUser}
        onOpenChange={(open) => !open && setEditMspUser(null)}
        mspUser={editMspUser}
        mspDetails={mspDetails}
        companyId={activeTenancy.id}
        onSuccess={async () => {
          setEditMspUser(null);
          // Refresh MSP details
          try {
            const { getDoc, doc } = await import('firebase/firestore');
            const customerRef = doc(db, 'customers', activeTenancy.id);
            const customerSnap = await getDoc(customerRef);
            if (customerSnap.exists()) {
              const data = customerSnap.data();
              setMspDetails(data.mspDetails || null);
            }
          } catch (error) {
            console.error('Error refreshing MSP details:', error);
          }
        }}
      />
    </div>
  );
}

// DED-1250: MSP Edit Dialog Component
interface MspEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mspUser: User | null;
  mspDetails: MspDetails | null;
  companyId: string;
  onSuccess: () => void;
}

function MspEditDialog({ open, onOpenChange, mspUser, mspDetails, companyId, onSuccess }: MspEditDialogProps) {
  const [saving, setSaving] = useState(false);
  const [mspName, setMspName] = useState<string>('');
  const [loadingName, setLoadingName] = useState(false);
  
  // Get the MSP partner details based on the user type
  const mspType = mspUser?.mspType;
  const partnerDetails = mspType === 'techPartner' 
    ? mspDetails?.techPartner 
    : mspDetails?.businessPartner;
  
  const [impersonationAllowed, setImpersonationAllowed] = useState(partnerDetails?.impersonationAllowed ?? false);
  
  // Fetch MSP name when dialog opens
  useEffect(() => {
    const fetchMspName = async () => {
      if (!partnerDetails?.msp || !open) return;
      
      setLoadingName(true);
      try {
        const details = await mspService.getMspDetails(partnerDetails.msp, companyId);
        setMspName(details.customerFriendlyName || details.customerName || 'Unknown MSP');
      } catch (error) {
        console.error('Error fetching MSP name:', error);
        setMspName('Unknown MSP');
      } finally {
        setLoadingName(false);
      }
    };
    
    fetchMspName();
  }, [partnerDetails?.msp, companyId, open]);
  
  // Reset impersonation state when dialog opens
  useEffect(() => {
    if (open && partnerDetails) {
      setImpersonationAllowed(partnerDetails.impersonationAllowed ?? false);
    }
  }, [open, partnerDetails]);
  
  const handleSave = async () => {
    if (!mspType || !partnerDetails) return;
    
    setSaving(true);
    try {
      const { updateDoc, doc } = await import('firebase/firestore');
      const customerRef = doc(db, 'customers', companyId);
      
      await updateDoc(customerRef, {
        [`mspDetails.${mspType}.impersonationAllowed`]: impersonationAllowed,
      });
      
      toast.success('MSP access settings updated successfully');
      onSuccess();
    } catch (error) {
      console.error('Error updating MSP settings:', error);
      toast.error('Failed to update MSP settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleRemoveMsp = async () => {
    if (!mspType || !partnerDetails) return;
    
    if (!confirm(`Are you sure you want to remove this ${mspType === 'techPartner' ? 'Tech Partner' : 'Business Partner'} MSP? This action cannot be undone.`)) {
      return;
    }
    
    setSaving(true);
    try {
      const { updateDoc, doc, deleteField } = await import('firebase/firestore');
      const customerRef = doc(db, 'customers', companyId);
      
      await updateDoc(customerRef, {
        [`mspDetails.${mspType}`]: deleteField(),
      });
      
      toast.success('MSP removed successfully');
      onSuccess();
    } catch (error) {
      console.error('Error removing MSP:', error);
      toast.error('Failed to remove MSP');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-white dark:bg-secondary">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {mspType === 'techPartner' ? 'Tech Partner' : 'Business Partner'} MSP Settings
          </DialogTitle>
          <DialogDescription>
            Manage access settings for this MSP partner.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          {/* MSP Name */}
          <div className="grid gap-2">
            <Label>MSP Organization</Label>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              {loadingName ? (
                <span className="text-muted-foreground">Loading...</span>
              ) : (
                <span className="font-medium">{mspName}</span>
              )}
            </div>
          </div>
          
          {/* Impersonation Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-0.5">
              <Label className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Allow Impersonation
              </Label>
              <p className="text-sm text-muted-foreground">
                When enabled, this MSP can access and manage your tenancy.
              </p>
            </div>
            <Switch
              checked={impersonationAllowed}
              onCheckedChange={setImpersonationAllowed}
              disabled={saving}
            />
          </div>
          
          {/* Warning when impersonation is enabled */}
          {impersonationAllowed && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-500 mt-0.5 shrink-0" />
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                This MSP will have access to view and manage your organization's data.
              </p>
            </div>
          )}
        </div>
        
        <div className="flex justify-between gap-2">
          <Button
            variant="destructive"
            onClick={handleRemoveMsp}
            disabled={saving}
          >
            Remove MSP
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface FormProps {
  companyId: string;
  onSuccess: () => void;
  editData?: User;
  isDrawer?: boolean;
  plan?: string; // DED-1444: Plan to determine if ISMS fields should be shown
}

function UserForm({ companyId, onSuccess, editData, isDrawer, plan }: FormProps) {
  // DED-1444: Hide ISMS Role and Committee for Starter plans (PA, E8, SMB, DISP)
  const isStarterPlan = plan?.toLowerCase().includes('starter');
  // DED-605: Include loading state to handle async data loading
  // DED-605: Removed teams - Team Settings should not be in User Management
  const { businessUnits, loading: orgStructureLoading } = useOrgStructure();
  const [saving, setSaving] = useState(false);

  // DED-605: Removed team field - Team Settings should not be in User Management
  const [form, setForm] = useState({
    email: editData?.email || '',
    firstName: editData?.firstName || '',
    lastName: editData?.lastName || '',
    role: editData?.roles?.owner ? 'owner' : 'user',
    ismsRole: editData?.ismsRole || 'User',
    ismsCommittee: editData?.ismsCommittee || '',
    businessUnit: editData?.businessUnit || '',
    assignPolicies: true,
  });

  const [originalEmail] = useState(editData?.email || '');

  const updateField = (name: string, value: string | boolean) => {
    setForm((f) => ({ ...f, [name]: value }));
  };

  const generatePassword = (length = 15) => {
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+[]{}|;:',.<>?";

    function generate() {
      let password = '';
      for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * charset.length);
        password += charset[randomIndex];
      }
      return password;
    }

    function validate(password: string) {
      const hasUppercase = /[A-Z]/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSpecialChar = /[!@#$%^&*()_+\[\]{}|;:',.<>?]/.test(password);

      return password.length >= length && hasUppercase && hasLowercase && hasNumber && hasSpecialChar;
    }

    let password: string;
    do {
      password = generate();
    } while (!validate(password));

    return password;
  };

  const saveUser = async () => {
    setSaving(true);

    try {
      if (editData) {
        // Existing user update flow
        await setDoc(doc(db, 'users', editData.id), {
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          roles: { [form.role]: true },
        }, { merge: true });

        if (form.email !== originalEmail) {
          const token = await auth.currentUser?.getIdToken();
          await fetch(`${import.meta.env.VITE_cloudFunctionURL}/deiterateInternalAPI/updateUserEmail`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Company-ID': companyId,
            },
            body: JSON.stringify({ newEmail: form.email, userID: editData.id }),
          });
        }

        // DED-605: Removed teamName - Team Settings should not be in User Management
        await setDoc(doc(db, 'customers', companyId), {
          customerOrg: {
            users: {
              [editData.id]: {
                businessUnit: form.businessUnit,
                ismsRole: form.ismsRole,
                ismsCommittee: form.ismsCommittee,
                lastUpdatedBy: auth.currentUser?.uid,
                role: form.role,
                userID: editData.id,
              },
            },
          },
        }, { merge: true });

        // User data will be automatically updated via the TenancyContext listener

        onSuccess();
      } else {
        // New user creation flow
        const password = generatePassword(15);

        const authWorkerApp = firebaseImport.initializeApp(app.options, 'auth-worker');
        const authWorkerAuth = firebaseImport.auth(authWorkerApp);

        // DO NOT await here if using firebase v8-style
        authWorkerAuth.setPersistence(firebaseAuth.Auth.Persistence.NONE);

        const userCredential = await authWorkerAuth.createUserWithEmailAndPassword(form.email, password);
        const newUID = userCredential.user.uid;
        const userEmail = userCredential.user.email;

        const idToken = await auth.currentUser?.getIdToken();

        const response = await fetch(`${import.meta.env.VITE_cloudFunctionURL}/deiterateInternalAPI/organisations/addUser`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
            'Company-ID': companyId,
          },
          // DED-605: Removed team - Team Settings should not be in User Management
          body: JSON.stringify({
            firebaseUID: newUID,
            email: userEmail,
            firstName: form.firstName,
            lastName: form.lastName,
            companyID: companyId,
            role: form.role,
            PAUser: false,
            assignPolicies: form.assignPolicies,
            businessUnit: form.businessUnit,
            committeeRole: form.ismsCommittee,
            ismsRole: form.ismsRole,
            addedBy: auth.currentUser?.uid,
            preserveMspRole: true,
          }),
        });

        if (!response.ok) {
          throw new Error(await response.text());
        }

        // DED-605: Removed teamName - Team Settings should not be in User Management
        await setDoc(doc(db, 'customers', companyId), {
          customerOrg: {
            users: {
              [newUID]: {
                businessUnit: form.businessUnit,
                ismsRole: form.ismsRole,
                ismsCommittee: form.ismsCommittee,
                lastUpdatedBy: auth.currentUser?.uid,
                role: form.role,
                userID: newUID,
              },
            },
          },
        }, { merge: true });

        // User data will be automatically updated via the TenancyContext listener

        await authWorkerAuth.signOut();
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error saving user:', err);
      alert(`Failed to save user: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };



  return (
    <div className="grid gap-4">
      {/* Basic Details */}
      <div className="grid gap-1">
        <Label htmlFor="firstName">First Name</Label>
        <Input id="firstName" value={form.firstName} onChange={(e) => updateField('firstName', e.target.value)} />
      </div>

      <div className="grid gap-1">
        <Label htmlFor="lastName">Last Name</Label>
        <Input id="lastName" value={form.lastName} onChange={(e) => updateField('lastName', e.target.value)} />
      </div>

      <div className="grid gap-1">
        <Label htmlFor="email">Email</Label>
        <Input id="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} type="email" />
      </div>

      {/* Business Unit - DED-605: Added loading state handling */}
      <div className="grid gap-1">
        <Label htmlFor="businessUnit">Business Unit</Label>
        <Select value={form.businessUnit} onValueChange={(v) => updateField('businessUnit', v)} disabled={orgStructureLoading}>
          <SelectTrigger id="businessUnit" className="w-full">
            <SelectValue placeholder={orgStructureLoading ? "Loading..." : "Select business unit"} />
          </SelectTrigger>
          <SelectContent>
            {businessUnits.map((b) => (
              <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!orgStructureLoading && businessUnits.length === 0 && (
          <p className="text-sm text-muted-foreground">No business units defined for this organisation.</p>
        )}
      </div>

      {/* DED-605: Team dropdown removed - Team Settings should not be in User Management */}

      {/* Roles */}
      <div className="grid gap-1">
        <Label htmlFor="role">de.iterate Role</Label>
        <Select value={form.role} onValueChange={(v) => updateField('role', v)}>
          <SelectTrigger id="role" className="w-full"><SelectValue placeholder="Select role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="owner">Company Admin</SelectItem>
            <SelectItem value="user">Policy Reader</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* DED-1444: Hide ISMS Role and Committee for Starter plans */}
      {!isStarterPlan && (
        <>
          <div className="grid gap-1">
            <Label htmlFor="ismsRole">ISMS Role</Label>
            <Select value={form.ismsRole} onValueChange={(v) => updateField('ismsRole', v)}>
              <SelectTrigger id="ismsRole" className="w-full"><SelectValue placeholder="Select ISMS role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Asset Owner">Asset Owner</SelectItem>
                <SelectItem value="Business Sponsor">Business Sponsor</SelectItem>
                <SelectItem value="User">User</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1">
            <Label htmlFor="ismsCommittee">ISMS Committee Role</Label>
            <Select value={form.ismsCommittee} onValueChange={(v) => updateField('ismsCommittee', v)}>
              <SelectTrigger id="ismsCommittee" className="w-full"><SelectValue placeholder="Select committee role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="Chair">Chair</SelectItem>
                <SelectItem value="Member">Member</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {/* Policy Assignment */}
      {!editData && (
        <div className="flex items-center gap-2 pt-2">
          <Checkbox
            id="assignPolicies"
            checked={form.assignPolicies}
            onCheckedChange={(checked) => updateField('assignPolicies', !!checked)}
          />
          <Label htmlFor="assignPolicies">Assign all policies</Label>
        </div>
      )}

      {/* Actions */}
      <div className={`pt-6 flex ${isDrawer ? 'justify-center' : 'justify-end'}`}>
        {isDrawer ? (
          <div className="flex w-full max-w-md gap-1">
            <Button
              type="button"
              variant="outline"
              onClick={onSuccess}
              className="w-1/2"
            >
              Cancel
            </Button>
            <Button
              onClick={saveUser}
              disabled={saving}
              className="w-1/2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save
            </Button>
          </div>
        ) : (
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={onSuccess}
            >
              Cancel
            </Button>
            <Button
              onClick={saveUser}
              disabled={saving}
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

type deleteUserConfirmationDialogProps = {
  show: boolean;
  callback: (confirmed: boolean) => void;
};
const DeleteUserConfirmationDialog = ({ show, callback }: deleteUserConfirmationDialogProps) => {
  return (
    <Dialog open={show}>
      <DialogContent className="bg-card">
        <DialogHeader>
          <DialogTitle>Delete User</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this user?
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => callback(false) }>Cancel</Button>
          <Button
            variant="destructive"
            onClick={() => callback(true)}
          >
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
